// /cartes — Public hub aggregating every appellation we cover with an
// interactive geoVINUM map. Each card links to the dedicated article (FR
// version) where visitors can explore the appellation in depth.
//
// SEO value: turns every appellation into an indexable doorway, with this
// page as the canonical "catalogue" entry point.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, ArrowRight, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import GeoVinumMap from "@/components/GeoVinumMap";
import { REGIONS, APPELLATION_REGION, OTHER_REGION_LABEL_KEY } from "@/lib/appellation_regions";
import { useLang, useT } from "@/lib/i18n";
import { renderInline } from "@/lib/renderInline";
import { apiFetch } from "@/lib/api";

// Diacritic-insensitive search — same logic as the live demo.
const norm = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const CartesPage = () => {
  const lang = useLang();
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeRegion, setActiveRegion] = useState("all");
  const [previewSlug, setPreviewSlug] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      // STRICT language filter — visitors only see appellations published in
      // their current language. We no longer fall back to other languages
      // because the user explicitly wants every listing (blog, atlas, cépages)
      // restricted to the visitor's locale.
      const res = await apiFetch(`/api/blog/appellations?language=${lang}`);
      const d = res.ok ? res.data : { items: [] };
      if (!cancelled) {
        setItems(d.items || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  // Sort by appellation name (the API already filters by language so there
  // are no cross-language duplicates to dedupe).
  const byAppellation = useMemo(() => {
    return [...items]
      .filter((it) => it.appellation)
      .sort((a, b) => a.appellation.localeCompare(b.appellation));
  }, [items]);

  // Filter by region + free-text query.
  const filtered = useMemo(() => {
    const q = norm(query);
    return byAppellation.filter((it) => {
      const reg = APPELLATION_REGION[it.appellation] || "other";
      if (activeRegion !== "all" && reg !== activeRegion) return false;
      if (!q) return true;
      const hay = norm(`${it.appellation} ${it.title} ${it.excerpt}`);
      return hay.includes(q);
    });
  }, [byAppellation, query, activeRegion]);

  // Group filtered cards by region (preserve REGIONS order).
  const grouped = useMemo(() => {
    const buckets = new Map(REGIONS.map((r) => [r.id, []]));
    buckets.set("other", []);
    for (const it of filtered) {
      const reg = APPELLATION_REGION[it.appellation] || "other";
      if (!buckets.has(reg)) buckets.set(reg, []);
      buckets.get(reg).push(it);
    }
    return Array.from(buckets.entries())
      .filter(([, arr]) => arr.length > 0)
      .map(([id, arr]) => ({
        id,
        labelKey: REGIONS.find((r) => r.id === id)?.labelKey || OTHER_REGION_LABEL_KEY,
        items: arr,
      }));
  }, [filtered]);

  // Region pills count — based on the post-search filter so users see how
  // many results each region has under the current query.
  const regionCounts = useMemo(() => {
    const counts = { all: 0 };
    const q = norm(query);
    for (const it of byAppellation) {
      const reg = APPELLATION_REGION[it.appellation] || "other";
      const hay = norm(`${it.appellation} ${it.title} ${it.excerpt}`);
      if (q && !hay.includes(q)) continue;
      counts.all += 1;
      counts[reg] = (counts[reg] || 0) + 1;
    }
    return counts;
  }, [byAppellation, query]);

  return (
    <Layout testId="cartes-page">
      <SEO
        title={t("cartes.seo_title")}
        description={t("cartes.seo_desc")}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[color:var(--gv-mist)] via-white to-[color:var(--gv-mist)]/40 border-b border-[color:var(--gv-line)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-[color:var(--gv-line)] text-[color:var(--gv-blue)] px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.15em] font-medium mb-5 shadow-sm">
            <MapPin size={11} /> {t("cartes.eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--gv-ink)] leading-[1.05] mb-5">
            {t("cartes.h1_line1")}<br/>{t("cartes.h1_line2")}
          </h1>
          <p className="text-[15px] md:text-[17px] text-[color:var(--gv-ink)]/70 max-w-2xl mx-auto leading-relaxed">
            {byAppellation.length > 0
              ? renderInline(t("cartes.subtitle"), [<strong key="n" className="text-[color:var(--gv-ink)]">{byAppellation.length}</strong>])
              : t("cartes.subtitle_empty")}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-[color:var(--gv-line)]" data-testid="cartes-filters">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4">
          <div className="relative mb-3 max-w-xl mx-auto">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("cartes.search_placeholder")}
              className="w-full h-11 pl-11 pr-4 rounded-[5px] border border-[color:var(--gv-line)] bg-white text-[14px] focus:outline-none focus:border-[color:var(--gv-pink)] transition-colors"
              aria-label={t("cartes.search_placeholder")}
              data-testid="cartes-search"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setActiveRegion("all")}
              className={`px-3 py-1 rounded-[5px] text-[12.5px] border transition-colors ${
                activeRegion === "all"
                  ? "bg-[color:var(--gv-ink)] border-[color:var(--gv-ink)] text-white"
                  : "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70 hover:border-[color:var(--gv-pink)]/50 hover:text-[color:var(--gv-pink)]"
              }`}
              data-testid="cartes-region-all"
            >
              {t("cartes.region_all")} <span className="opacity-70">({regionCounts.all || 0})</span>
            </button>
            {REGIONS.map((r) => {
              const c = regionCounts[r.id] || 0;
              if (c === 0) return null;
              const active = activeRegion === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setActiveRegion(r.id)}
                  className={`px-3 py-1 rounded-[5px] text-[12.5px] border transition-colors ${
                    active
                      ? "bg-[color:var(--gv-pink)] border-[color:var(--gv-pink)] text-white"
                      : "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70 hover:border-[color:var(--gv-pink)]/50 hover:text-[color:var(--gv-pink)]"
                  }`}
                  data-testid={`cartes-region-${r.id}`}
                >
                  {t(r.labelKey)} <span className="opacity-70">({c})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="text-center py-20 text-[color:var(--gv-ink)]/40 text-[13px]" data-testid="cartes-loading">{t("cartes.loading")}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="cartes-empty">
              <Sparkles className="mx-auto text-[color:var(--gv-ink)]/25 mb-3" size={28} />
              <p className="text-[14px] text-[color:var(--gv-ink)]/55">
                {t("cartes.empty")} <button onClick={() => { setQuery(""); setActiveRegion("all"); }} className="text-[color:var(--gv-pink)] hover:underline">{t("cartes.empty_reset")}</button>
              </p>
            </div>
          ) : (
            grouped.map((bucket) => (
              <div key={bucket.id} className="mb-12" data-testid={`cartes-bucket-${bucket.id}`}>
                <div className="flex items-baseline justify-between mb-5 pb-3 border-b border-[color:var(--gv-line)]">
                  <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">{t(bucket.labelKey)}</h2>
                  <span className="text-[12px] text-[color:var(--gv-ink)]/45">{t(bucket.items.length > 1 ? "cartes.count_many" : "cartes.count_one").replace("{0}", bucket.items.length)}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {bucket.items.map((it) => (
                    <article
                      key={it.slug}
                      className="group bg-white border border-[color:var(--gv-line)] rounded-[10px] overflow-hidden hover:border-[color:var(--gv-pink)]/40 hover:shadow-lg transition-all flex flex-col"
                      data-testid={`cartes-card-${it.appellation}`}
                    >
                      <div className="p-5 flex-1 flex flex-col">
                        <div className="text-[10.5px] uppercase tracking-[0.13em] text-[color:var(--gv-pink)] mb-1.5">{t("cartes.card.eyebrow")}</div>
                        <h3 className="font-display text-[19px] text-[color:var(--gv-ink)] mb-2 leading-tight">{it.appellation}</h3>
                        <p className="text-[13px] text-[color:var(--gv-ink)]/65 leading-relaxed mb-4 line-clamp-3 flex-1">{it.excerpt}</p>
                        <div className="flex items-center justify-between mt-auto">
                          <button
                            onClick={() => setPreviewSlug(previewSlug === it.slug ? null : it.slug)}
                            className="text-[12.5px] text-[color:var(--gv-blue)] hover:text-[color:var(--gv-pink)] inline-flex items-center gap-1.5 transition-colors"
                            data-testid={`cartes-card-preview-${it.appellation}`}
                          >
                            <MapPin size={12} />
                            {previewSlug === it.slug ? t("cartes.card.hide_preview") : t("cartes.card.preview")}
                          </button>
                          <Link
                            to={`/blog/${it.slug}`}
                            className="text-[12.5px] text-[color:var(--gv-ink)]/75 hover:text-[color:var(--gv-pink)] inline-flex items-center gap-1 transition-colors"
                          >
                            {t("cartes.card.read")} <ArrowRight size={12} />
                          </Link>
                        </div>
                        {previewSlug === it.slug && (
                          <div className="mt-4" data-testid={`cartes-card-map-${it.appellation}`}>
                            <GeoVinumMap appellation={it.appellation} />
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA — for partners who want their own iframe */}
      <section className="bg-[color:var(--gv-ink)] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-[5px] text-[10.5px] uppercase tracking-[0.15em] font-medium mb-5">
            <Sparkles size={11} /> {t("cartes.cta.eyebrow")}
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-4">{t("cartes.cta.title")}</h2>
          <p className="text-[14.5px] md:text-[15.5px] text-white/70 mb-7 max-w-2xl mx-auto">
            {t("cartes.cta.subtitle")}
          </p>
          <Link to="/products/iframe" className="inline-flex items-center gap-2 bg-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink)]/90 text-white px-6 py-3 rounded-[5px] text-[13.5px] transition-colors">
            {t("cartes.cta.button")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default CartesPage;
